import { css } from '@emotion/react'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import { fonts } from '@opallabs/pulsar'
import { colors } from '@opallabs/spectrum'
import { AssignmentDate } from '../AssignmentHelpers/AssignmentDate'
import { AssignmentIcon } from '../AssignmentHelpers/AssignmentIcon'
import { AssignmentPopover } from '../AssignmentHelpers/AssignmentPopover'
import {
  ContentLiveDateDialogue,
  DueDateDialogue,
} from '../AssignmentHelpers/AssignmentPopover/dialogues'
import { AssignmentText } from '../AssignmentHelpers/AssignmentText'
import { backgroundByStatus } from '../AssignmentHelpers/helpers'
import {
  approvalContext,
  approvalContextText,
  approvalDetail,
  approvalDetailText,
  approvalItem,
  approvalItemRow,
} from './styles'
import { ApprovalProps, ApprovalStatus } from './types'

const DATE_FORMAT_COMPLETED = 'E MMM d yyyy, h:mm aaa'

export const ApprovalItem: React.FC<ApprovalProps> = ({
  approvalName = 'Approval Phase',
  approvalStatus,
  assignmentStatus,
  commentText,
  completedAt,
  contextLiveDate,
  contextName,
  dueDate,
  hardDueDate,
  iconColor,
  iconName,
  onItemClick,
  overdue = false,
}) => {
  // @TODO - update the recentlyCompleted state locally instead of in the Backbone code
  const [recentlyCompleted] = useState(false)

  const hasBeenCompleted = useMemo(() => {
    return (
      completedAt !== undefined && approvalStatus !== ApprovalStatus.incomplete
    )
  }, [completedAt, approvalStatus])

  const backgroundStyles = useMemo(
    () => backgroundByStatus(assignmentStatus),
    [assignmentStatus]
  )

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <li
      onClick={onItemClick}
      css={css([approvalItem(recentlyCompleted), backgroundStyles])}
    >
      <div css={approvalItemRow}>
        <div css={approvalContext}>
          <AssignmentIcon iconName={iconName} color={iconColor} />
          <div css={approvalContextText}>{contextName}</div>
        </div>
        <AssignmentIcon
          css={{ margin: '0 10px' }}
          iconName="chevron-e"
          color={colors.n6.base}
          height={10}
          width={10}
        />
        <div css={approvalDetail}>
          <div css={approvalDetailText}>{approvalName}</div>
        </div>
        {approvalStatus === ApprovalStatus.incomplete &&
        dueDate !== undefined ? (
          <AssignmentPopover
            trigger={
              <AssignmentDate
                dueAtDate={hardDueDate}
                dueDate={dueDate}
                overdue={overdue}
              />
            }
            triggerEvent="hover"
          >
            {hardDueDate ? (
              <DueDateDialogue
                dueDate={dueDate}
                contentLiveDate={contextLiveDate}
              />
            ) : (
              <ContentLiveDateDialogue dueDate={dueDate} />
            )}
          </AssignmentPopover>
        ) : null}
      </div>
      {hasBeenCompleted && commentText ? (
        <div css={approvalItemRow}>
          <AssignmentText
            text={commentText}
            styleOverride={{
              color: colors.n4.base,
              margin: '8px 0 0 0',
              fontFamily: fonts.proxima,
              fontWeight: 400,
            }}
          />
        </div>
      ) : null}
      {hasBeenCompleted && completedAt ? (
        <div css={approvalItemRow}>
          <AssignmentText
            text={`${approvalStatus} on ${format(
              completedAt,
              DATE_FORMAT_COMPLETED
            )}`}
            styleOverride={{ color: colors.n6.base, margin: '4px 0 0 0' }}
          />
        </div>
      ) : null}
    </li>
  )
}
